/**
 * A aba "Motivos de perda" de Etapas do funil (058).
 *
 * A lista que a revenda escreve para dizer por que perdeu uma venda. Motivo
 * não se apaga, se DESATIVA: sai da lista de escolha e continua nomeando as
 * perdas já registradas com ele. Por isso a tela oferece "Desativar" e
 * "Reativar", e nunca "Remover".
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MotivoDePerda } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, Cartao, Chip } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

export function MotivosDePerdaDoFunil(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const motivos = useQuery({
    queryKey: chaves.motivosDePerda(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.motivosDePerda({ sinal: signal }),
  });
  const invalidar = () => void cache.invalidateQueries({ queryKey: chaves.motivosDePerda() });

  const [novo, definirNovo] = useState('');
  const criar = useMutation({
    mutationFn: (texto: string) => api!.criarMotivoDePerda({ corpo: { texto } }),
    onSuccess: () => {
      definirNovo('');
      invalidar();
    },
  });

  return (
    <div role="tabpanel" aria-label="Motivos de perda" className="admin__funis-painel">
      <p className="admin__nota">
        Os motivos que a equipe escolhe ao marcar uma venda como perdida. Com eles, dá para ver por
        que as vendas estão sendo perdidas. Um motivo desativado sai da lista de escolha e continua
        nas perdas já registradas.
      </p>

      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (novo.trim() !== '') criar.mutate(novo.trim());
        }}
      >
        <Campo
          id="motivo-novo"
          rotulo="Novo motivo"
          type="text"
          value={novo}
          maxLength={80}
          placeholder="Ex.: Preço, Crédito negado, Comprou no concorrente"
          onChange={(e) => definirNovo(e.currentTarget.value)}
        />
        {criar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(criar.error, { generica: 'Não foi possível acrescentar o motivo.' })}
          </Faixa>
        )}
        <span className="admin__form-acoes">
          <Botao
            type="submit"
            desabilitado={novo.trim() === '' || criar.isPending || api === null}
            motivo={criar.isPending ? 'Acrescentando…' : 'Escreva o motivo antes.'}
          >
            {criar.isPending ? 'Acrescentando…' : 'Acrescentar'}
          </Botao>
        </span>
      </Cartao>

      {motivos.isPending && <p>Buscando os motivos…</p>}
      {motivos.isError && <Faixa tom="bloqueado">Não foi possível buscar os motivos.</Faixa>}
      {motivos.data?.itens.length === 0 && (
        <p className="admin__nota">
          Nenhum motivo ainda. Enquanto a lista estiver vazia, a venda perdida é registrada só com a
          observação de quem a marcou.
        </p>
      )}

      {(motivos.data?.itens.length ?? 0) > 0 && (
        <ul className="admin__motivos" aria-label="Motivos de perda">
          {motivos.data!.itens.map((motivo) => (
            <LinhaDoMotivo key={motivo.id} motivo={motivo} aoMudar={invalidar} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LinhaDoMotivo({
  motivo,
  aoMudar,
}: {
  motivo: MotivoDePerda;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const [editando, definirEditando] = useState(false);
  const [texto, definirTexto] = useState(motivo.texto);

  const editar = useMutation({
    mutationFn: (mudanca: { texto?: string; ativo?: boolean }) =>
      api!.editarMotivoDePerda({ caminho: { id: motivo.id }, corpo: mudanca }),
    onSuccess: () => {
      definirEditando(false);
      aoMudar();
    },
  });

  return (
    <Cartao como="li" variante="linha" parte="admin__motivo">
      {editando ? (
        <form
          className="admin__motivo-edicao"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (texto.trim() !== '') editar.mutate({ texto: texto.trim() });
          }}
        >
          <Campo
            rotulo={`Novo texto de "${motivo.texto}"`}
            type="text"
            value={texto}
            maxLength={80}
            autoFocus
            ajuda="As perdas já registradas continuam com o texto de antes."
            onChange={(e) => definirTexto(e.currentTarget.value)}
          />
          <span className="admin__form-acoes">
            <Botao variante="fantasma" miudo onClick={() => definirEditando(false)}>
              Cancelar
            </Botao>
            <Botao
              type="submit"
              miudo
              desabilitado={texto.trim() === '' || editar.isPending}
              motivo="Salvando…"
            >
              Salvar
            </Botao>
          </span>
        </form>
      ) : (
        <>
          <span className="admin__motivo-texto">
            {motivo.texto}
            {!motivo.ativo && <Chip title="Não aparece para escolher">desativado</Chip>}
          </span>
          <span className="admin__form-acoes">
            <Botao
              miudo
              variante="fantasma"
              aria-label={`Renomear ${motivo.texto}`}
              onClick={() => {
                definirTexto(motivo.texto);
                definirEditando(true);
              }}
            >
              Renomear
            </Botao>
            <Botao
              miudo
              variante="fantasma"
              desabilitado={editar.isPending}
              motivo="Salvando…"
              aria-label={`${motivo.ativo ? 'Desativar' : 'Reativar'} ${motivo.texto}`}
              onClick={() => editar.mutate({ ativo: !motivo.ativo })}
            >
              {motivo.ativo ? 'Desativar' : 'Reativar'}
            </Botao>
          </span>
        </>
      )}
      {editar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(editar.error, { generica: 'Não foi possível salvar o motivo.' })}
        </Faixa>
      )}
    </Cartao>
  );
}
